import {
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  input,
  OnInit,
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

import {
  finalize,
  Observable,
  pipe,
  Subject,
  takeUntil,
  tap,
  UnaryFunction,
} from 'rxjs';
import {
  CreateFormBuilderResponseCommand,
  CustomResult,
  FormBuilderFieldResponseModel,
  FormBuilderFieldTypes,
  GetFormBuilderByIdDto,
} from 'src/app/core/api/models';
import { EventTypes } from 'src/app/core/constants/event-types';
import { OrderByPipe } from 'src/app/core/pipes/order-by.pipe';
import { ApiNotificationService } from 'src/app/core/services/api-notification.service';
import { AppConfigService } from 'src/app/core/services/config.service';
import { ConsoleService } from 'src/app/core/services/console.service';
import { DialogService } from 'src/app/core/services/dialog.service';
import { AppDomService } from 'src/app/core/services/dom.service';
import { LoggerService } from 'src/app/core/services/logger.service';
import { ShowImagesModule } from 'src/app/shared/components/show-images/show-images.module';
import { FormBtnsModule } from 'src/app/shared/global/form-btns/form-btns.module';
import { FormBuildersService } from '../../../core/api/services/form-builders.service';
import { FormGeneratorFieldComponent } from './form-generator-field/form-generator-field.component';
import { FormThemeColorDirective } from './form-theme-color.directive';
import { UserMobileComponent } from './user-mobile/user-mobile.component';

@Component({
  selector: 'form-generator',
  templateUrl: './form-generator.component.html',
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    FormGeneratorFieldComponent,
    ShowImagesModule,
    FormBtnsModule,
    OrderByPipe,
    FormThemeColorDirective,
  ],
  standalone: true,
})
export class FormGeneratorComponent implements OnInit {
  //#region properties

  loggerService = inject(LoggerService);
  cd = inject(ChangeDetectorRef);
  apiNotificationService = inject(ApiNotificationService);
  @Input() isModalMode: boolean = false;

  tagName: 'form';
  formsConfig = input.required<GetFormBuilderByIdDto>();
  imageUrl: string;
  responses: FormBuilderFieldResponseModel[] = [];
  dialogService = inject(DialogService);
  protected onDestroy$ = new Subject<void>();
  loading: boolean = false;

  get formBuilderFieldTypes() {
    return FormBuilderFieldTypes;
  }
  //#endregion

  constructor(private currentService: FormBuildersService) {}

  ngOnInit() {
    if (this.formsConfig()?.backgroundImagePath)
      this.imageUrl =
        AppConfigService.config.apiUrl +
        '/' +
        this.formsConfig()?.backgroundImagePath;
  }

  emitData(data: any) {
    this.responses = data;
  }

  onSubmit() {
    if (!this.checkFormIsValid()) return;

    this.getUserMobileNumber();
  }

  //#region validation
  checkFormIsValid() {
    try {
      let formElement = document.querySelector('#form');
      if (!formElement) return false;

      this.markAllFieldsAsTouched(formElement);
      if (formElement.getElementsByClassName('ng-invalid').length == 0)
        return true;

      if (formElement) {
        let firstInvalidControl =
          formElement.getElementsByClassName('ng-invalid')[0];
        const yOffset = -170;
        const y =
          firstInvalidControl.getBoundingClientRect().top +
          window.pageYOffset +
          yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }

      return false;
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.dashboard);
    }
  }

  markAllFieldsAsTouched(formElement) {
    const inputs = formElement.querySelectorAll('input, select, textarea');
    inputs.forEach((input: any) => {
      input.classList.add('touched'); // Add a custom class for styling
      input.reportValidity?.(); // Show validation messages (for native elements)
    });

    // If using custom Angular components like `form-field`, trigger their validation
    const customFields = formElement.querySelectorAll(
      'form-field, custom-select-search, datepicker, timepicker'
    );
    customFields.forEach((customField: any) => {
      customField.classList.add('touched'); // Add CSS class
      if (customField.validate) {
        customField.validate(); // Call validation method if available
      }
    });
  }
  //#endregion

  getUserMobileNumber() {
    try {
      this.unSubs();

      let data = {
        title: 'common.mobile',
      };

      const modalResult = this.dialogService.openModal({
        component: UserMobileComponent,
        data: data,
      });

      modalResult
        .afterClosed()
        .pipe(
          takeUntil(this.onDestroy$),
          tap(() => {
            AppDomService.removeModalScrollClassFromHtml();
          })
        )
        .subscribe((res) => {
          if (res.isSucceed) {
            this.createApi({
              formBuilderId: this.formsConfig()?.id,
              mobile: res.mobile,
              fieldResponses: this.responses,
            });
            this.cd.detectChanges();
          }
        });
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.dashboard);
    }
  }

  unSubs() {
    this.onDestroy$.next();
  }

  createApi(model: CreateFormBuilderResponseCommand) {
    this.unSubs();

    this.loading = true;

    this.currentService
      .formBuildersCreateFormBuilderResponse({ body: model })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseObserver);
    this.setEmptyValues();
  }

  setEmptyValues() {
    this.loading = false;
    this.responses = [];
  }

  basePipeOperators = <T>() =>
    pipe(
      finalize(() => {
        this.loading = false;
        this.responses = [];
      }),
      takeUntil(this.onDestroy$)
    ) as UnaryFunction<Observable<T>, Observable<T>>;

  baseObserver = {
    next: (result: CustomResult) => {
      this.apiNotificationService.notifyMessage({ result });
    },
    error: (error) => {
      ConsoleService.error('error', error);
    },
    complete: () => (this.loading = false),
  };
}
